import { Link, useLocation } from 'react-router-dom';
import { StyledNav } from './Nav.styled';
import { useState } from 'react';
import Hamburger from '../utils/Hamburger/Hamburger';
import { useAuthContext } from '../../hooks/useAuthContext';
import { PathName } from '../../types/firestore';
import { Anchor } from '@mantine/core';
import { logout } from '../../utils/logout';

const Nav = () => {
  const { user, isAdmin } = useAuthContext();

  const [showNav, setShowNav] = useState(false);
  const [toggleHamburger, setToggleHamburger] = useState(false);

  const location = useLocation();

  // Aria current page attributes that adjust depending on the current active page state
  const getAriaCurrentPage = (path: PathName): { 'aria-current'?: 'page' } => {
    return location.pathname === path ? { 'aria-current': 'page' } : {};
  };

  // Shows or hides the Nav menu on mobile
  const toggleNav = () => {
    setShowNav((prevState) => !prevState);
    setToggleHamburger((prevState) => !prevState);
  };

  // Specifically used to close the menu, rather than toggle
  const closeNav = () => {
    setShowNav(false);
    setToggleHamburger(false);
  };

  // Combine the logout function with confirmation to the user of successful logout
  const performLogout = async () => {
    closeNav();
    logout();
  };

  return (
    <StyledNav $user={user} className="Nav" aria-label="Main menu" role="navigation">
      <ul className={`${showNav ? 'Nav__list active' : 'Nav__list'}`} role="menubar">
        {!user && (
          <>
            <li className="Nav__list-item" role="menuitem">
              <Anchor
                component={Link}
                onClick={closeNav}
                className="Nav__link Nav__link--std Nav__link--homepage"
                to="/features"
                {...getAriaCurrentPage('/features')}
              >
                Features
              </Anchor>
            </li>
            <li className="Nav__list-item" role="menuitem">
              <Anchor
                component={Link}
                onClick={closeNav}
                className="Nav__link Nav__link--std Nav__link--homepage"
                to="/faq"
                {...getAriaCurrentPage('/faq')}
              >
                FAQ
              </Anchor>
            </li>
            <li className="Nav__list-item" role="menuitem">
              <Anchor
                component={Link}
                onClick={closeNav}
                className="Nav__link Nav__link--std Nav__link--homepage"
                to="/login"
                {...getAriaCurrentPage('/login')}
              >
                Log in
              </Anchor>
            </li>
            <li className="Nav__list-item" role="menuitem">
              {/* To style separately to log in btn, add class 'Nav__link--signup */}
              <Anchor
                component={Link}
                onClick={closeNav}
                className="Nav__link Nav__link--std Nav__link--homepage"
                to="/signup"
                {...getAriaCurrentPage('/signup')}
              >
                Sign up
              </Anchor>
            </li>
          </>
        )}

        {user && (
          <>
            <li className="Nav__list-item" role="none">
              <Anchor
                component={Link}
                className="Nav__link Nav__link--std"
                to="/prescription/new"
                role="menuitem"
                onClick={closeNav}
                {...getAriaCurrentPage('/prescription/new')}
                state={{ newRx: true }}
              >
                New Rx
              </Anchor>
            </li>
            <li className="Nav__list-item" role="none">
              <Anchor
                component={Link}
                onClick={closeNav}
                className="Nav__link Nav__link--std"
                to="/scripts"
                role="menuitem"
                {...getAriaCurrentPage('/scripts')}
              >
                My scripts
              </Anchor>
            </li>
            <li className="Nav__list-item" role="none">
              <Anchor
                component={Link}
                onClick={closeNav}
                className="Nav__link Nav__link--std"
                to="/practices"
                role="menuitem"
                {...getAriaCurrentPage('/practices')}
              >
                Practices
              </Anchor>
            </li>
            <li className="Nav__list-item" role="none">
              <Anchor
                component={Link}
                onClick={closeNav}
                className="Nav__link Nav__link--std"
                to="/settings"
                role="menuitem"
                {...getAriaCurrentPage('/settings')}
              >
                Settings
              </Anchor>
            </li>
            {isAdmin && (
              <li className="Nav__list-item" role="none">
                <Anchor
                  component={Link}
                  onClick={closeNav}
                  className="Nav__link Nav__link--std"
                  to="/admin"
                  role="menuitem"
                  {...getAriaCurrentPage('/admin')}
                >
                  Admin
                </Anchor>
              </li>
            )}

            <li className="Nav__list-item" role="none">
              <button className="Nav__link Nav__link--std" onClick={performLogout} role="menuitem">
                Log out
              </button>
            </li>
          </>
        )}
      </ul>

      <Hamburger
        loggedIn={user ? true : false}
        handleClick={toggleNav}
        expanded={toggleHamburger}
      />
    </StyledNav>
  );
};

export default Nav;
